import { useId, type ReactNode } from 'react';

import { InfoIcon, TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  InterruptionModeSelect,
  PreToolSpeechSelect,
} from '@/components/atoms/ToolSettingsSelect/ToolSettingsSelect';
import type { ToolSettings } from '@/shared/types/toolSettings';

import type { McpTool } from '../hooks/useMcpServers';

type SettingSectionProps = { id: string; title: string; description: string; children: ReactNode };

const SettingSection = ({ id, title, description, children }: SettingSectionProps) => (
  <section className="flex flex-col gap-2">
    <div className="flex flex-col gap-0.5">
      <h3 id={id} className="text-sm leading-5 font-semibold text-foreground">
        {title}
      </h3>
      <p className="text-xs leading-4 text-muted-foreground">{description}</p>
    </div>
    {children}
  </section>
);

// The MCP spec gives `destructiveHint` meaning only when `readOnlyHint` is false.
const isReadOnly = (tool: McpTool) => tool.annotations?.readOnlyHint === true;

/** Points at the server's own hint when the chosen mode disagrees with it. Never changes it. */
const AnnotationNote = ({ tool, settings }: { tool: McpTool; settings: ToolSettings }) => {
  const { t } = useTranslation();
  const isAllowed = settings.interruption_mode === 'allow';

  if (!isReadOnly(tool) && tool.annotations?.destructiveHint === true && isAllowed) {
    return (
      <p className="flex items-start gap-1.5 text-xs leading-4 text-foreground">
        <TriangleAlertIcon className="size-4 shrink-0 text-warning" aria-hidden="true" />
        {t('mcp.destructive_hint')}
      </p>
    );
  }
  if (isReadOnly(tool) && !isAllowed) {
    return (
      <p className="flex items-start gap-1.5 text-xs leading-4 text-muted-foreground">
        <InfoIcon className="size-4 shrink-0" aria-hidden="true" />
        {t('mcp.read_only_hint')}
      </p>
    );
  }
  return null;
};

type ToolVoiceTabProps = {
  tool: McpTool;
  settings: ToolSettings;
  canWrite: boolean;
  onChangeSettings: (settings: ToolSettings) => void;
};

const ToolVoiceTab = ({ tool, settings, canWrite, onChangeSettings }: ToolVoiceTabProps) => {
  const { t } = useTranslation();
  const interruptionId = useId();
  const speechId = useId();

  return (
    <div className="-mx-2 flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-2">
      <p className="text-xs leading-4 text-muted-foreground">{t('mcp.voice_intro')}</p>
      <SettingSection
        id={interruptionId}
        title={t('mcp.interruptions')}
        description={t('mcp.interruptions_description')}
      >
        <InterruptionModeSelect
          aria-labelledby={interruptionId}
          value={settings.interruption_mode}
          isReadOnlyTool={isReadOnly(tool)}
          disabled={!canWrite}
          onChange={(interruption_mode) => onChangeSettings({ ...settings, interruption_mode })}
        />
        <AnnotationNote tool={tool} settings={settings} />
      </SettingSection>
      <SettingSection
        id={speechId}
        title={t('voice_tool_settings.pre_tool_speech')}
        description={t('mcp.pre_tool_speech_description')}
      >
        <PreToolSpeechSelect
          aria-labelledby={speechId}
          value={settings.pre_tool_speech}
          disabled={!canWrite}
          onChange={(pre_tool_speech) => onChangeSettings({ ...settings, pre_tool_speech })}
        />
      </SettingSection>
    </div>
  );
};

export default ToolVoiceTab;
